import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import CompetitionCard from "@/components/CompetitionCard";
import InstagramSection from "@/components/InstagramSection";
import TikTokSection from "@/components/TikTokSection";
import { ArrowRight, Trophy, Zap, ShieldCheck, Sparkles } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_27ac1c15-2a1f-4f1d-9928-0405b6a7c5e2/artifacts/v5ckfoe3_ChatGPT%20Image%20Jul%207%2C%202026%2C%2004_19_07%20PM.png";

export default function Home() {
  const [comps, setComps] = useState([]);
  const [stats, setStats] = useState({ live_raffles: 0, tickets_sold: 0, latest_winner: null });
  useEffect(() => {
    api.get("/competitions?status=live").then((r) => setComps(r.data)).catch(() => {});
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const featured = comps.slice(0, 4);

  return (
    <div data-testid="home-page">
      {/* DISCORD BANNER */}
      <a
        href="https://discord.gg/9GZgfuGXy"
        target="_blank"
        rel="noopener noreferrer"
        className="block relative overflow-hidden border-b border-[#5865F2]/40 group"
        style={{ background: "linear-gradient(90deg, rgba(88,101,242,0.22) 0%, rgba(88,101,242,0.08) 45%, rgba(34,255,102,0.08) 100%)" }}
        data-testid="discord-banner"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-3 flex items-center justify-center gap-3 flex-wrap text-center">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="#5865F2" aria-hidden="true">
            <path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.058a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.099.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.331c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
          </svg>
          <span className="text-sm text-white font-bold tracking-wide">JOIN OUR DISCORD</span>
          <span className="text-sm text-white/60 hidden sm:inline">— live draw alerts, exclusive drops &amp; the UFR community</span>
          <span className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1 rounded-full bg-[#5865F2] text-white group-hover:bg-[#6b76ff] transition-colors">
            Join now <ArrowRight size={12} />
          </span>
        </div>
      </a>

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 ufr-grid-bg opacity-60" />
        <div className="absolute -top-40 -right-40 w-[700px] h-[700px] rounded-full blur-3xl opacity-30"
             style={{ background: "radial-gradient(circle, rgba(34,255,102,0.55), transparent 60%)" }} />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="ufr-chip mb-6" data-testid="hero-chip">
              <span className="live-dot" /> Skill-based football card raffles
            </div>
            <h1 className="font-display text-5xl sm:text-6xl md:text-7xl text-white leading-[0.95]">
              WIN PREMIUM <br />
              <span className="text-[color:var(--ufr-green)] glow-text">FOOTBALL CARDS</span><br />
              FROM £0.50
            </h1>
            <p className="text-white/70 mt-6 text-base md:text-lg max-w-xl">
              Answer the trivia, grab your ticket, and walk away with 1/1s, Grails and sealed product.
            </p>
            <div className="mt-8 flex flex-wrap gap-3" data-testid="hero-ctas">
              <Link to="/competitions" className="btn-ufr-primary px-7 py-3.5 text-sm inline-flex items-center gap-2" data-testid="hero-cta-enter">
                Enter live raffles <ArrowRight size={16} />
              </Link>
              <Link to="/how-it-works" className="btn-ufr-ghost px-7 py-3.5 text-sm" data-testid="hero-cta-how">
                How it works
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
              <Stat label="Live raffles" value={stats.live_raffles || "—"} />
              <Stat label="Tickets sold" value={(stats.tickets_sold || 0).toLocaleString()} />
              <Stat
                label={stats.latest_winner ? "Latest winner" : "Winners so far"}
                value={stats.latest_winner ? `#${stats.latest_winner.winner.ticket_number}` : "0"}
              />
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative aspect-square w-full max-w-md mx-auto">
              <div className="absolute inset-0 rounded-xl overflow-hidden border border-[color:var(--ufr-green)]/40 shadow-green-soft">
                <img src={LOGO} alt="UFR" className="absolute inset-0 w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TICKER */}
      <section className="border-y border-white/5 bg-black/60 overflow-hidden" data-testid="ticker-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-4 flex items-center gap-6 overflow-x-auto">
          <span className="ufr-chip shrink-0">Recent winners</span>
          {stats.recent_winners && stats.recent_winners.length > 0 ? (
            stats.recent_winners.map((w, i) => {
              const email = w.winner?.user_email || "";
              const masked = email ? email.split("@")[0].slice(0, 2).toUpperCase() + "***" : "Winner";
              return (
                <span key={i} className="shrink-0 text-sm text-white/70 whitespace-nowrap">
                  <span className="text-[color:var(--ufr-green)] font-bold mr-2">●</span>
                  {masked} — {w.title} (#{w.winner?.ticket_number})
                </span>
              );
            })
          ) : (
            <span className="shrink-0 text-sm text-white/50 whitespace-nowrap italic">
              Winners will appear here once the first draws happen — be one of the first.
            </span>
          )}
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24" data-testid="featured-section">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--ufr-green)] font-bold">Live now</div>
            <h2 className="font-display text-4xl md:text-5xl text-white mt-2">Featured raffles</h2>
          </div>
          <Link to="/competitions" className="hidden md:inline-flex items-center gap-1 text-sm font-bold text-white/80 hover:text-[color:var(--ufr-green)]">
            View all <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="featured-grid">
          {featured.map((c) => <CompetitionCard key={c.id} c={c} />)}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24 relative" data-testid="how-section">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--ufr-green)] font-bold">Skill-based</div>
        <h2 className="font-display text-4xl md:text-5xl text-white mt-2 mb-12">How it works</h2>

        <div className="grid md:grid-cols-3 gap-6">
          <Step n="01" title="Answer the trivia" icon={<Zap size={22} />}
                text="Each raffle has a multiple-choice football question. Get it right to qualify — it's skill, not a lottery." />
          <Step n="02" title="Grab your tickets" icon={<Sparkles size={22} />}
                text="Pay from £0.50 per ticket. Stack entries to boost your odds on rookies, autos and grail hits." />
          <Step n="03" title="Live draw, real cards" icon={<Trophy size={22} />}
                text="Once the timer hits zero, we draw live on stream and ship the slabbed card straight to your door." />
        </div>
      </section>

      {/* TRUST */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24" data-testid="trust-section">
        <div className="ufr-card rounded-md p-8 md:p-14">
          <div>
            <ShieldCheck size={36} className="text-[color:var(--ufr-green)]" />
            <h3 className="font-display text-3xl md:text-4xl text-white mt-4">Built by collectors, for collectors.</h3>
            <p className="text-white/70 mt-4 text-sm md:text-base max-w-md">
              Skill based competition: a correct trivia answer is required before payment. Authentic slabbed cards. Live draws. Transparent ticket counts.
            </p>
            <Link to="/competitions" className="btn-ufr-primary px-7 py-3 text-sm inline-flex items-center gap-2 mt-6" data-testid="trust-cta">
              Browse raffles <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <InstagramSection />
      <TikTokSection />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-display text-2xl text-white">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold mt-1">{label}</div>
    </div>
  );
}

function Step({ n, title, text, icon }) {
  return (
    <div className="ufr-card rounded-md p-6 relative" data-testid={`step-${n}`}>
      <div className="flex items-center justify-between">
        <div className="text-[color:var(--ufr-green)]">{icon}</div>
        <div className="font-display text-5xl text-white/10">{n}</div>
      </div>
      <div className="font-display text-2xl text-white mt-4">{title}</div>
      <p className="text-white/60 text-sm mt-2 leading-relaxed">{text}</p>
    </div>
  );
}
